'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const LAST_UPDATED = 'August 27, 2026';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] justify-center sm:justify-start sm:min-w-0 sm:px-2"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <AppLogo size={26} />
            <span className="font-semibold text-sm text-foreground">Naija Explorer</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Title block */}
        <div className="mb-10">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Shield size={18} className="text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Privacy Policy</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Last updated: {LAST_UPDATED}
          </p>
          <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/15">
            <p className="text-sm text-foreground leading-relaxed">
              Naija Explorer is an educational platform designed for children aged 6–14, operated under parental supervision. 
              We take the privacy of children seriously. This policy explains what data we collect, how we use it, and how parents can control it.
            </p>
          </div>
        </div>

        <div className="prose-custom space-y-8 text-sm text-foreground leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">1. Who We Are</h2>
            <p className="text-muted-foreground">
              Naija Explorer is an educational application that helps Nigerian children discover their culture, languages, stories and life skills.
              The platform is operated by <strong className="text-foreground">Emmanuel Chiemezie (Possyble)</strong>, 
              based in <strong className="text-foreground">Nigeria</strong>.
            </p>
            <p className="text-muted-foreground mt-3">
              Contact:{' '}
              <a href="mailto:naijaexplorer@aol.com" className="text-primary hover:underline font-medium">naijaexplorer@aol.com</a>
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">2. The Parent / Child Account Model</h2>
            <p className="text-muted-foreground mb-3">
              Naija Explorer uses a <strong className="text-foreground">parent-controlled account model</strong>:
            </p>
            <ul className="space-y-2 text-muted-foreground list-none pl-0">
              {[
                'A parent or guardian creates the account using their own email address and password.',
                'The parent then creates one or more child explorer profiles within that account.',
                'Each child profile has a display name (chosen by the parent) and an age group (6–8, 9–11, or 12–14).',
                'Children do not create their own accounts and do not provide their own email addresses.',
                'All account management — including deleting profiles or requesting data removal — is done by the parent.',
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">3. What Data We Collect</h2>

            <h3 className="font-semibold text-foreground mb-2 mt-4">3a. Parent Account Data</h3>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>Email address (used for authentication and account recovery)</li>
              <li>Full name (used to personalise the parent dashboard)</li>
              <li>Encrypted password (we never store passwords in plain text)</li>
            </ul>

            <h3 className="font-semibold text-foreground mb-2 mt-4">3b. Child Profile Data</h3>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>Display name (chosen by the parent — does not need to be the child&apos;s real name)</li>
              <li>Age group (6–8, 9–11, or 12–14) — used to tailor content difficulty</li>
              <li>Avatar selection (emoji-based, no photos)</li>
            </ul>

            <h3 className="font-semibold text-foreground mb-2 mt-4">3c. Child Learning &amp; Progress Data</h3>
            <p className="text-muted-foreground mb-2">
              We store educational progress data linked to each child profile. This includes:
            </p>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>Lessons and stories completed</li>
              <li>States and language items explored</li>
              <li>Game scores and session results</li>
              <li>XP (experience points) earned and current level</li>
              <li>Achievements and badges unlocked</li>
              <li>Daily learning streaks</li>
              <li>Quiz answers and challenge completions</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              This data is used solely to personalise the child&apos;s learning experience and to provide parents with progress reports. 
              It is never sold or shared with advertisers.
            </p>

            <h3 className="font-semibold text-foreground mb-2 mt-4">3d. Technical Data</h3>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>Session tokens (for keeping you signed in)</li>
              <li>Device type and browser (for compatibility and error diagnosis)</li>
              <li>PWA install preference (stored locally on your device only)</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">4. How We Use Your Data</h2>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>To authenticate parents and maintain secure sessions</li>
              <li>To personalise educational content to each child&apos;s age group and progress</li>
              <li>To display progress reports and insights to parents</li>
              <li>To award achievements and maintain learning streaks</li>
              <li>To improve the platform based on aggregate (non-personal) usage patterns</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do <strong className="text-foreground">not</strong> use child data for advertising, profiling, or any commercial purpose unrelated to the educational service.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">5. Data Storage &amp; Security</h2>
            <p className="text-muted-foreground">
              All data is stored securely using Supabase (a PostgreSQL-based cloud database). 
              Access is protected by Row-Level Security policies that ensure each parent can only access their own account and child profiles. 
              Passwords are hashed and never stored in plain text. Connections are encrypted via HTTPS/TLS.
            </p>
            <p className="text-muted-foreground mt-3">
              While we take reasonable technical precautions, no internet service can guarantee absolute security. 
              We encourage parents to use a strong, unique password.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">6. Data Sharing</h2>
            <p className="text-muted-foreground">
              We do <strong className="text-foreground">not</strong> sell, rent, or share personal data with third parties for marketing purposes.
            </p>
            <p className="text-muted-foreground mt-3">
              We may share data only in the following limited circumstances:
            </p>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5 mt-2">
              <li>With infrastructure providers (e.g. Supabase) strictly for operating the service</li>
              <li>If required by law or a valid legal process</li>
              <li>To protect the safety of users or the public</li>
            </ul>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">7. Children&apos;s Privacy</h2>
            <p className="text-muted-foreground">
              Naija Explorer is designed for children aged 6–14 and is operated under a parent-controlled model. 
              We do not knowingly collect personal information directly from children. 
              All accounts are created and managed by parents or guardians.
            </p>
            <p className="text-muted-foreground mt-3">
              If you believe a child has provided personal information without parental consent, please contact us immediately at{' '}
              <a href="mailto:naijaexplorer@aol.com" className="text-primary hover:underline font-medium">naijaexplorer@aol.com</a> and we will promptly delete it.
            </p>
            <div className="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <strong>Note:</strong> Naija Explorer is not independently certified under COPPA, GDPR-K, or any other children&apos;s privacy regulation. 
                Parents are responsible for supervising their children&apos;s use of the platform.
              </p>
            </div>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">8. Your Rights as a Parent</h2>
            <p className="text-muted-foreground mb-3">As the account holder, you have the right to:</p>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>Access all data stored for your account and child profiles</li>
              <li>Correct inaccurate information</li>
              <li>Delete a child profile and all associated progress data</li>
              <li>Delete your parent account entirely</li>
              <li>Request a copy of your data</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise these rights, contact us at{' '}
              <a href="mailto:naijaexplorer@aol.com" className="text-primary hover:underline font-medium">naijaexplorer@aol.com</a>.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">9. Cookies &amp; Local Storage</h2>
            <p className="text-muted-foreground">
              We use browser cookies and local storage for:
            </p>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5 mt-2">
              <li>Maintaining your login session (authentication cookies)</li>
              <li>Remembering your theme preference (light/dark mode)</li>
              <li>Remembering whether you have dismissed the PWA install prompt</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do not use advertising cookies or third-party tracking cookies.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">10. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. When we do, we will update the &quot;Last updated&quot; date at the top of this page. 
              Continued use of Naija Explorer after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">11. Contact Us</h2>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/40 border border-border">
              <Mail size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground text-sm mb-1">Privacy enquiries</p>
                <p className="text-muted-foreground text-sm">
                  Email:{' '}
                  <a href="mailto:naijaexplorer@aol.com" className="text-primary hover:underline font-medium">naijaexplorer@aol.com</a>
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  We aim to respond to all privacy requests within 30 days.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
          <Link href="/terms" className="text-primary hover:underline font-medium">Terms of Use</Link>
          <Link href="/contact" className="text-primary hover:underline font-medium">Contact / Support</Link>
          <Link href="/" className="text-muted-foreground hover:text-foreground">← Back to Naija Explorer</Link>
        </div>
      </main>
    </div>
  );
}
